// The picker stays live after a job has been submitted, so what it shows can drift from what the
// job on screen was run for. A page remembers the window at submit time and labels results,
// previews and return trips with that one, never with the picker's current value.
// Pure: no React, router or Firebase imports.
import type { Granularity } from '../config/products';
import type { IsoDay } from './dateWindow';

/** The two instants (ISO, UTC) a job was submitted with, exactly as they went into the request. */
export interface WindowUtc {
  start: string;
  end: string;
}

/** A WindowUtc from router state, or null when it is missing or not two non-empty strings. */
export function asWindowUtc(v: unknown): WindowUtc | null {
  if (typeof v !== 'object' || v === null) return null;
  const { start, end } = v as { start?: unknown; end?: unknown };
  return typeof start === 'string' && start !== '' && typeof end === 'string' && end !== '' ? { start, end } : null;
}

/** The delivery day and time resolution a job was submitted for, and the window it sent. */
export interface SubmittedWindow {
  day: IsoDay;
  granularity: Granularity;
  /** The request's start_time and end_time. Travels with the job so the CSV can state them. */
  windowUtc?: WindowUtc;
}

/** What a data-window picker currently shows. */
export interface PickerWindow {
  day: IsoDay | null;
  granularity: Granularity;
}

/** The window to remember on a return trip: both parts must have been handed back. */
export function submittedFromScope(scope: {
  day: IsoDay | null;
  granularity: Granularity | null;
  windowUtc?: WindowUtc | null;
}): SubmittedWindow | null {
  if (!scope.day || !scope.granularity) return null;
  return scope.windowUtc
    ? { day: scope.day, granularity: scope.granularity, windowUtc: scope.windowUtc }
    : { day: scope.day, granularity: scope.granularity };
}

/** True when the job on screen was run for a different day or resolution than the picker shows now. */
export function pickerDiffersFromJob(submitted: SubmittedWindow | null, picker: PickerWindow): boolean {
  if (!submitted) return false;
  return submitted.day !== picker.day || submitted.granularity !== picker.granularity;
}
